/**
 * @file view-technical-details.tsx
 * @module app/modules/uProcure/master/technical-details/view-technical-details
 * @description View technical details page for uProcure master technical details module.
 * This page displays technical fields organized by category tabs, allowing users to
 * manage (view, edit, delete) technical requirements for different product categories.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.1.1
 * @version 2.1.2
 *
 * @todo Warehouse refetch: fetchTabs/fetchActiveTabData run on mount only; refetch when
 *   selectedWarehouse changes (warehouse is passed to APIs — uprocure-admin does not).
 * @todo Duplicate page titles: index.tsx PageTitle plus h1 rendered twice in this component
 *   (lines ~237 and ~280) — remove duplicates.
 * @todo Route-level permission guard: hasModulePermission on mount only; add PrivateRoutes
 *   wrapper if required for direct URL access.
 * @todo add-technical-details checks hasPermission(Add) only; add hasModulePermission(TechnicalDetails)
 *   for consistency with this view page.
 * @todo dropdown/DropdownMenu1.tsx is @deprecated with empty Link targets — remove or wire. Copied to app/modules/procurement/pages/master/technical-details/view-technical-details.tsx. This file still serves the existing /UProcure routes.
 */

import { Card, Col, Nav, Row, Tab } from "react-bootstrap";
import EditTechnicalField from "../../../../modals/edit-technical-field";
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Add,
  DateSelection,
  Delete,
  Edit,
  MultiLineText,
  MultiSelection,
  PAGE_LIMIT,
  SingleLineText,
  SingleSelection,
  TechnicalDetails,
} from "../../../../../utils/constants";
import { master } from "../../../../../api/apiEndPoints";
import APICallService from "../../../../../api/apiCallService";
import Loader from "../../../../../Global/loader";
import { CustomSelectTable } from "../../../../custom/Select/CustomSelectTable";
import ThreeDotMenu from "../../../../../_admin/assets/media/svg_uTrade/three-dot.svg";
import { getKey, setKey } from "../../../../../Global/history";
import { listTechnicalDetails } from "../../../../../utils/storeString";
import { success } from "../../../../../Global/toast";
import { masterToast } from "../../../../../utils/toast";
import DeleteModal from "../../../../modals/delete";
import { useAuth } from "../../../auth";
import Method from "../../../../../utils/methods";
// import { warehouse as warehouseStoreString } from "../../../../utils/storeString";
import { useWarehouse } from "../../../../context/WarehouseContext";

const ViewTechnicalDetails = () => {
  const { selectedWarehouse } = useWarehouse();
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [showTechnicalFiledModal, setShowTechnicalFiledModal] = useState(false);
  const [active, setActive] = useState(0);
  const [activeId, setActiveId] = useState("");
  const [page, setPage] = useState(1);
  const [pageLimit, setPageLimit] = useState(PAGE_LIMIT);
  const [editData, setEditData] = useState([]);
  const handleActive = async (event: any, id: string) => {
    setKey(listTechnicalDetails.currentTab, event);
    setKey(listTechnicalDetails.currentTabId, id.toString(), false);
    setActive(event);
    setActiveId(id);
    await fetchActiveTabData(id);
    setLoading(false);
  };
  const [tabs, setTabs] = useState([]);
  const [activeTabData, setActiveTabData] = useState([]);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedTab, setSelectedTab] = useState<any>();
  const [error, setError] = useState("");
  const [deleteLoader, setDeleteLoader] = useState(false);
  const [fetchLoader, setFetchLoader] = useState(false);
  const [loading, setLoading] = useState(false);
  const [show, setShow] = useState(false);
  const [id, setId] = useState("");
  useEffect(() => {
    (async () => {
      if (!Method.hasModulePermission(TechnicalDetails, currentUser)) {
        return window.history.back();
      }
      await fetchTabs();
    })();
  }, []);

  const fetchTabs = async () => {
    setFetchLoader(true);
    let apiService = new APICallService(
      master.technicalCategories,
      "",
      "",
      "",
      "",
      "",
      TechnicalDetails,
      selectedWarehouse?.value || null,
    );
    let response = await apiService.callAPI();
    if (response.records) {
      setTabs(response.records);
      if (getKey(listTechnicalDetails.currentTabId)) {
        let key: any = getKey(listTechnicalDetails.currentTabId);
        setActiveId(key);
        await fetchActiveTabData(key);
      } else {
        setActiveId(response.records[0]._id);
        await fetchActiveTabData(response.records[0]._id);
      }
    }
    setFetchLoader(false);
  };

  const fetchActiveTabData = async (id: string) => {
    setLoading(true);
    let apiService = new APICallService(
      master.technicalDetailFeild,
      id,
      "",
      "",
      "",
      "",
      TechnicalDetails,
      selectedWarehouse?.value || null,
    );
    let response = await apiService.callAPI();
    if (response.records) {
      setActiveTabData(response.records);
    }
    setLoading(false);
  };
  const handleClose = async (techData: any, edited?: boolean) => {
    if (edited) {
      let data: any = { ...techData };
      await fetchActiveTabData(data.activeId);
    }
    setShowTechnicalFiledModal(false);
  };
  const openMenuOnClick = async (id: any) => {
    // if (show) {
    //   setId('')
    //   setShow(false)
    // } else {
    //   setId(id)
    //   setShow(true)
    // }
    setId(id);
    setShow(true);
    // navigate('/master/edit-business-certificates', { state: data })
  };
  const onMenuClose = async () => {
    setId("");
    setShow(false);
  };
  const onMenuOpen = async (id: any) => {
    setId(id);
    setShow(true);
  };
  const handleOption = async (event: any, data: any) => {
    if (event.value === 1) {
      data["options"] = data["options"].map((val: any) => {
        return { ...val, value: val.title, label: val.title };
      });
      data["activeId"] = activeId;
      setEditData(data);
      setShowTechnicalFiledModal(true);
    } else if (event.value === 2) {
      setDeleteModalOpen(true);
      setSelectedTab(data);
    } else if (event.value === 0) {
    }
  };

  const handleDelete = async () => {
    setDeleteLoader(true);
    let apiService = new APICallService(
      master.deleteTechnicalDetails,
      selectedTab?._id,
      "",
      "",
      "",
      true,
      TechnicalDetails,
      selectedWarehouse?.value || null,
    );
    let response = await apiService.callAPI();
    if (response.error) {
      setError(response.error);
    } else {
      setPage(1);
      fetchTabs();
      success(masterToast.deleteTechnical);
      setDeleteModalOpen(false);
    }
    setDeleteLoader(false);
  };
  const handleModalHide = () => {
    setDeleteModalOpen(false);
    setError("");
  };

  return (
    <>
      {deleteModalOpen && (
        <DeleteModal
          show={deleteModalOpen}
          onHide={handleModalHide}
          service={selectedTab}
          handleDelete={handleDelete}
          error={error}
          loading={deleteLoader}
          serviceName="technical detail"
          message={`Are you sure you want to delete ${selectedTab.name} technical detail?`}
          warnMessage={`By continuing, the ${selectedTab.name} technical detail will
          be removed permanently`}
        />
      )}
      {showTechnicalFiledModal ? (
        <EditTechnicalField
          show={showTechnicalFiledModal}
          handleClose={handleClose}
          editData={editData}
          moduleName={TechnicalDetails}
          category={
            tabs.find((item: any) => item._id == activeId)
              ? tabs.find((item: any) => item._id == activeId)
              : {}
          }
        />
      ) : (
        ""
      )}
      <div className="p-5">
        <Row className="align-items-center mb-6">
          <Col md className="align-self-center mb-5">
            <h1 className="fs-22 fw-bolder mb-0">Technical details</h1>
          </Col>
          {fetchLoader ? (
            <></>
          ) : activeTabData.length > 0 ? (
            <Col md={"auto"} className="text-right mb-5">
              {Method.hasPermission(TechnicalDetails, Add, currentUser) ? (
                <Link
                  to="/UProcure/master/technical-details/add-technical-details"
                  className="uw-btn uw-btn-primary uw-btn-lg btn btn-primary mh-md-50px btn-lg fs-16 fw-600"
                >
                  Add new technical detail
                </Link>
              ) : (
                <></>
              )}
            </Col>
          ) : (
            <>
              <Col lg={12} className="mt-2">
                {Method.hasPermission(TechnicalDetails, Add, currentUser) ? (
                  <div className="border border-r10px p-md-9 p-7">
                    <h2 className="fs-22 fw-bolder">
                      Start adding technical details!
                    </h2>
                    <p className="fs-18 fw-500 mb-md-8 mb-5">
                      You can define required technical details for each
                      category.
                    </p>
                    <Link
                      to="/master-old/technical-details/add-technical-details"
                      className="uw-btn uw-btn-primary uw-btn-lg btn btn-primary btn-lg"
                    >
                      Add technical detail
                    </Link>
                  </div>
                ) : (
                  <></>
                )}
              </Col>
            </>
          )}
          {/* <Col sm>
          <h1 className="fs-22 fw-bolder">Technical details</h1>
        </Col>
        <Col sm={'auto'}>
          <Link
            to="/master/technical-details/add-technical-details"
            className="btn btn-primary btn-lg"
          >
            Add new technical detail
          </Link>
        </Col> */}
        </Row>
        <Row>
          <Col lg={12} className="custom-tabContainer">
            {fetchLoader ? (
              <>
                <div className="d-flex justify-content-center">
                  <Loader loading={fetchLoader} />
                </div>
              </>
            ) : activeTabData.length > 0 ? (
              <Tab.Container id="left-tabs-example" defaultActiveKey="1">
                <Row className="variant-categories">
                  <Col lg={"auto"} className="my-2 mb-6">
                    <div className="bg-light border-r8px p-3 px-md-6">
                      <Nav variant="pills">
                        {tabs.map((val: any, index: number) => {
                          return (
                            <Nav.Item>
                              <Nav.Link
                                eventKey={index}
                                // className={
                                //   active === index ? "check-after" : ""
                                // }
                                onClick={() => {
                                  handleActive(index, val._id);
                                }}
                                active={activeId === val._id.toString()}
                              >
                                {val.title}
                              </Nav.Link>
                            </Nav.Item>
                          );
                        })}
                      </Nav>
                    </div>
                  </Col>
                  <Col lg={12}>
                    <Tab.Content>
                      <Tab.Pane eventKey={active} active={true}>
                        <Card className="biz-certificate">
                          <Card.Body className="py-3 overflow-hidden">
                            <div className="table-responsive">
                              <table className="table align-middle gs-0 gy-4">
                                <thead>
                                  <tr className="fs-16 fw-600 align-middle h-55px">
                                    <th className="min-w-250px">Field name</th>
                                    <th className="min-w-215px">Input type</th>
                                    <th className="min-w-lg-425px min-w-300px">
                                      Selection options
                                    </th>
                                    <th className="text-end min-w-lg-50px"></th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {loading ? (
                                    <>
                                      <tr>
                                        <td colSpan={3}>
                                          <div className="d-flex justify-content-center">
                                            <Loader loading={loading} />
                                          </div>
                                        </td>
                                      </tr>
                                    </>
                                  ) : (
                                    <>
                                      {" "}
                                      {activeTabData.map(
                                        (activeVal: any, index: number) => {
                                          return (
                                            <tr className="border-bottom">
                                              <td>{activeVal.name}</td>
                                              {activeVal.type ===
                                              SingleLineText ? (
                                                <td>Single line textbox </td>
                                              ) : (
                                                ""
                                              )}
                                              {activeVal.type ===
                                              MultiLineText ? (
                                                <td>Multi line textbox </td>
                                              ) : (
                                                ""
                                              )}
                                              {activeVal.type ===
                                              SingleSelection ? (
                                                <td>Single selection </td>
                                              ) : (
                                                ""
                                              )}
                                              {activeVal.type ===
                                              MultiSelection ? (
                                                <td>Multi selection </td>
                                              ) : (
                                                ""
                                              )}
                                              {activeVal.type ===
                                              DateSelection ? (
                                                <td>Date selection </td>
                                              ) : (
                                                ""
                                              )}
                                              <td>
                                                {activeVal.type ===
                                                  SingleSelection ||
                                                activeVal.type ===
                                                  MultiSelection ? (
                                                  <div className="d-flex flex-wrap">
                                                    {activeVal.options.map(
                                                      (
                                                        optionsVal: any,
                                                        optionIndex: number,
                                                      ) => {
                                                        return (
                                                          <span className="badge badge-light custom-badge fw-600">
                                                            {optionsVal.title}
                                                          </span>
                                                        );
                                                      },
                                                    )}
                                                  </div>
                                                ) : (
                                                  ""
                                                )}
                                              </td>
                                              <td>
                                                {Method.hasPermission(
                                                  TechnicalDetails,
                                                  Edit,
                                                  currentUser,
                                                ) ||
                                                Method.hasPermission(
                                                  TechnicalDetails,
                                                  Delete,
                                                  currentUser,
                                                ) ? (
                                                  <CustomSelectTable
                                                    marginLeft={"-90px"}
                                                    placeholder={
                                                      <img
                                                        className="img-fluid"
                                                        width={24}
                                                        height={7}
                                                        src={ThreeDotMenu}
                                                        alt=""
                                                      />
                                                    }
                                                    backgroundColor="white"
                                                    default={{
                                                      label: (
                                                        <img
                                                          className="img-fluid"
                                                          width={24}
                                                          height={7}
                                                          src={ThreeDotMenu}
                                                          alt=""
                                                        />
                                                      ),
                                                      value: (
                                                        <img
                                                          className="img-fluid"
                                                          width={24}
                                                          height={7}
                                                          src={ThreeDotMenu}
                                                          alt=""
                                                        />
                                                      ),
                                                    }}
                                                    options={[
                                                      ...(Method.hasPermission(
                                                        TechnicalDetails,
                                                        Edit,
                                                        currentUser,
                                                      )
                                                        ? [
                                                            {
                                                              label: (
                                                                <button className="btn btn-link fs-14 fw-500 text-black ms-3 p-4">
                                                                  Edit details
                                                                </button>
                                                              ),
                                                              value: 1,
                                                            },
                                                          ]
                                                        : []),
                                                      ...(Method.hasPermission(
                                                        TechnicalDetails,
                                                        Delete,
                                                        currentUser,
                                                      )
                                                        ? [
                                                            {
                                                              label: (
                                                                <button className="btn btn-link fs-14 fw-500 text-danger ms-3 p-4">
                                                                  Delete details
                                                                </button>
                                                              ),
                                                              value: 2,
                                                            },
                                                          ]
                                                        : []),
                                                    ]}
                                                    show={
                                                      show &&
                                                      activeVal._id === id
                                                    }
                                                    onMenuClose={() => {
                                                      onMenuClose();
                                                    }}
                                                    openMenuOnClick={() => {
                                                      openMenuOnClick(
                                                        activeVal._id,
                                                      );
                                                    }}
                                                    onMenuOpen={() => {
                                                      onMenuOpen(activeVal._id);
                                                    }}
                                                    onChange={(event: any) => {
                                                      handleOption(
                                                        event,
                                                        activeVal,
                                                      );
                                                    }}
                                                    isOptionDisabled={(
                                                      option: any,
                                                    ) => option.value === 3}
                                                  />
                                                ) : (
                                                  <></>
                                                )}
                                              </td>
                                            </tr>
                                          );
                                        },
                                      )}
                                    </>
                                  )}
                                </tbody>
                              </table>
                            </div>
                          </Card.Body>
                        </Card>
                      </Tab.Pane>
                    </Tab.Content>
                  </Col>
                </Row>
              </Tab.Container>
            ) : (
              <></>
            )}
          </Col>
        </Row>
      </div>
    </>
  );
};
export default ViewTechnicalDetails;
